/**
 * Higher-Order Component (HOC) for React Router v6 Navigation
 *
 * This HOC provides a bridge between React Router v6's useNavigate hook and class-based components
 * during our migration from React Router v5 to v6. It injects a 'history' prop that mimics the
 * old React Router v5 history API, allowing class components to navigate without refactoring.
 *
 * Usage:
 *   export const MyComponent = withNavigate(MyClassComponent);
 *
 * Migration Path:
 *   1. Replace withRouter HOC with this withNavigate HOC for class components
 *   2. Class components can continue using this.props.history.push()
 *   3. When converting to functional components, remove this HOC and use useNavigate() directly
 *   4. Once all components are functional, this file can be safely deleted
 *
 * Note: This is a temporary solution to ease migration. The end goal is to convert all
 * components to functional components and use the useNavigate hook directly.
 */

import { React } from '../../Imports';
import { useNavigate } from 'react-router-dom';

export interface WithNavigateProps {
    history: {
        push: (path: string) => void;
    };
}

export function withNavigate<P extends WithNavigateProps>(
    WrappedComponent: React.ComponentType<P>,
): React.ComponentType<Omit<P, keyof WithNavigateProps>> {
    return function WithNavigateComponent(props: Omit<P, keyof WithNavigateProps>) {
        const navigate = useNavigate();

        const history = {
            push: navigate,
        };

        return <WrappedComponent {...(props as P)} history={history} />;
    };
}